import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import type { EmployeeUserResponse, HealthInsuranceEmployeeConfigModel } from '@/api/model'
import { usePatchApiCompaniesCurrentServicemodulesInsuranceServiceModuleIdEmployeeconfigs } from '@/api/generated/company-management-user/company-management-user'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { notify } from '@/lib/notify'

interface FormValues {
  healthInsuranceNumber: string
}

const defaultValues: FormValues = {
  healthInsuranceNumber: '',
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceModuleId: number
  companyId: number
  employee: EmployeeUserResponse | null
  config?: HealthInsuranceEmployeeConfigModel
  onSuccess?: () => void
}

export function HealthInsuranceEmployeeDialog({ open, onOpenChange, serviceModuleId, companyId, employee, config, onSuccess }: Props) {
  const { t } = useTranslation(['service-modules-insurance', 'common'])

  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm<FormValues>({ defaultValues })

  const { mutate, isPending } = usePatchApiCompaniesCurrentServicemodulesInsuranceServiceModuleIdEmployeeconfigs()

  useEffect(() => {
    if (open) {
      reset({ healthInsuranceNumber: config?.healthInsuranceNumber ?? '' })
    }
  }, [open, config, reset])

  function onSubmit(values: FormValues) {
    if (!employee?.id) return

    const payload = {
      id: config?.id ?? 0,
      employeeUserId: employee.id,
      companyId,
      serviceModuleId,
      healthInsuranceNumber: values.healthInsuranceNumber || '',
    }

    mutate(
      { serviceModuleId, data: payload },
      {
        onSuccess: () => {
          notify.success(t('insurance.dialog.saveSuccess'))
          onSuccess?.()
          onOpenChange(false)
        },
        onError: (error) => {
          notify.apiError(error, t('insurance.dialog.saveError'))
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('insurance.dialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label>{t('columns.firstName', { ns: 'common' })}</Label>
            <Input value={employee?.firstName ?? '—'} disabled />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('columns.lastName', { ns: 'common' })}</Label>
            <Input value={employee?.lastName ?? '—'} disabled />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="insurance-number">{t('insurance.dialog.insuranceNumber')}</Label>
            <Input
              id="insurance-number"
              {...register('healthInsuranceNumber')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending || isSubmitting}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={isPending || isSubmitting}>
            {(isPending || isSubmitting) && <Loader2 className="size-4 animate-spin" />}
            {t('buttons.save', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
